// ─── L'area del maestro: i contratti ─────────────────────────────────────────
//
// 🗣️ 18/09/2026: *«vorrei sviluppare un piano dedicato ai maestri… si deve
// vedere bene sul cellulare perché i maestri non usano un computer»*.
//
// 🚨⭐⭐ **QUESTI SCHEMI SONO UNA BARRIERA, non una comodità di tipi.** Il
// serializzatore di Fastify manda fuori **solo** ciò che lo schema di risposta
// nomina: quello che non è scritto qui non esce, anche se il deposito l'ha
// letto e la funzione l'ha in mano.
// ⇒ Per questo l'area maestro non riusa gli schemi della segreteria. Riusarne
//   uno vorrebbe dire che il giorno in cui qualcuno aggiunge un campo a quello
//   schema — un telefono, un saldo, una riga di cassa — quel campo comincia a
//   uscire **anche di qui**, senza che nessuno abbia toccato questo file.
// 📌 *Uno schema riusato è un canale riusato.*
import { z } from 'zod';
import { trattenutaDelMaestroSchema } from './organizzatore.js';
import { dataSchema } from './circolo.js';
import { campoAParoleSchema } from './listini.js';
import { tariffaLezioneSchema } from './tariffe-lezioni.js';
/**
 * Chi sono, per l'area maestro.
 *
 * ⚖️ La stessa cosa sta anche in `profiloSchema.maestro` (⇒ `auth.ts`), e non è
 * un doppione per distrazione: il profilo serve a `App.tsx` per scegliere
 * **quale applicazione montare**, prima di qualunque altra chiamata; questa
 * rotta è la prova che la porta si apre davvero — cioè che c'è un legame e non
 * solo un ricordo nel gettone.
 */
export const ioMaestroSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
});
/**
 * Una banda del MIO perimetro, come la vede il maestro.
 *
 * 🚨 **Non è `bandaMaestroSchema`**, e la differenza è il motivo per cui questo
 * file esiste (⇒ l'intestazione): quello è il contratto della **segreteria**,
 * che configura: porta l'`id` della riga e il suo `active`, perché lì si
 * cancella e si spegne. Qui non si configura niente — si **legge dove si può
 * mettersi** — quindi:
 *   · niente `active`: una banda spenta non è un perimetro più corto, è una
 *     banda che non c'è. Filtrarla nella query e mandarla lo stesso sarebbe
 *     mandare una cosa da interpretare;
 *   · niente `id`: il maestro non cita mai una banda — prenota uno **spazio**,
 *     non una banda. Un identificativo che nessuno usa è un identificativo su
 *     cui qualcuno, un giorno, apre una rotta;
 *   · in più il **nome** del campo, che di là non serve (la segreteria ha
 *     l'elenco dei campi sotto mano) e di qua sì: un uuid su un telefono non
 *     dice a nessun maestro su quale campo può andare.
 */
export const bandaDelMioPerimetroSchema = z.object({
    /** Nullo = tutti i campi, e allora `courtName` è nullo pure lui. */
    courtId: z.uuid().nullable(),
    courtName: z.string().nullable(),
    /** Ogni settimana, quel giorno. Nullo per un giorno solo (🎓 18/10/2026). */
    weekday: z.number().int().min(0).max(6).nullable(),
    /** 🎓 18/10/2026: un giorno solo, `2026-10-25`. */
    onDate: z.string().nullable(),
    startsAtTime: z.string(),
    endsAtTime: z.string(),
});
export const mioPerimetroSchema = z.object({ items: z.array(bandaDelMioPerimetroSchema) });
// ── La sua giornata ──────────────────────────────────────────────────────────
/**
 * Una persona in campo, **come la vede il maestro**.
 *
 * 🚨 **Non è `giocatoreSchema`**, e le differenze sono tutte decisioni:
 *   · niente `memberId`: al maestro serve per togliere qualcuno, e per quello
 *     c'è l'`id` della riga in campo. Un identificativo del socio che viaggia
 *     senza servire è la chiave con cui, un giorno, qualcuno chiederà la scheda
 *     di quella persona da qui;
 *   · niente `incassatoCents`: 🗣️ *«vede gli importi, non incassa»* — l'importo
 *     **a carico** è cosa deve la persona, e si vede; quanto è **entrato in
 *     cassa** è il libro della segreteria, e non passa da questa porta;
 *   · niente `shortName` e niente `position`: sono due cose della griglia, e di
 *     qua la griglia non c'è (⇒ `AreaMaestro.tsx`).
 */
export const allievoInCampoSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    /** 💶 Quanto deve questa persona. Un numero da leggere, mai da toccare. */
    amountDueCents: z.number().int(),
    /**
     * 🚨⭐⭐ **NON C'ERA — e l'importo qui sopra resta suo.** 🗣️ *«Il corso si
     * paga al mese»* (19/09) ⇒ si paga il **posto**, non la presenza.
     *
     * ⚖️ È l'unico campo di questo schema che il maestro **scrive** invece di
     * leggere soltanto, e ha senso che sia lui: è l'unico che sa chi c'era.
     */
    assente: z.boolean(),
    /**
     * ⭐ È fra gli **iscritti al corso** di questa volta.
     *
     * ⚠️ **Sceglie il BOTTONE, e senza di lui la schermata ne mostrerebbe uno
     * sbagliato**: su una volta di corso un iscritto si segna *assente*, ma un
     * allievo aggiunto a mano a quella sola volta non è un iscritto — e
     * segnarlo assente lo lascerebbe a pagare un posto che non è suo.
     * 📏 E un bottone per riga non è un gusto: `.elenco-allievi li` è tre
     * colonne, e a 320 px una quarta non ci sta.
     */
    iscritto: z.boolean(),
    /**
     * 🆕⭐⭐ **NON È UN SOCIO — tappa ⑤, 20/09/2026.** Un nome scritto a mano da
     * bordo campo, ammesso **solo** su una lezione singola (⇒ `maestro/ospiti.ts`,
     * che è il posto dove quella regola vive davvero).
     *
     * ⚖️ **Perché un sì/no e non il `memberId` che manca.** La riga «niente
     * `memberId`» qui sopra **resta intera**, e questo campo non la scalfisce:
     * dice *se* quella persona è del circolo, non *quale* persona è. La domanda
     * a cui serve rispondere è «questo nome va scritto «· ospite»?», e per quella
     * un identificativo non serve.
     * 📌 *Mandare la chiave per rispondere a una domanda sì/no è mandare la
     *    chiave: quello che esce non è il bisogno, è ciò che si aveva in mano.*
     *
     * ⚠️ **E non si deduce a valle da `amountDueCents` o dal nome**: un ospite
     * può chiamarsi come un socio — è il caso che rende necessaria l'etichetta.
     */
    ospite: z.boolean(),
});
/**
 * Una sua lezione (o un suo corso), come gliela si racconta.
 *
 * ⚠️ **`courtName` e non `courtId` soltanto**, per la stessa ragione delle
 * bande del perimetro: a bordo campo un uuid non dice su quale campo andare.
 * L'`id` resta perché la schermata ci torna sopra, il nome perché è ciò che si
 * legge.
 *
 * ⛔ **Cosa NON c'è, e non è una dimenticanza**: `notes` della segreteria, la
 * cassa, la proposta di prezzo, la serie. Le prime due non lo riguardano; le
 * altre due arrivano quando arriveranno le rotte che le usano — uno schema che
 * porta un campo che nessuno mostra è un campo che qualcuno mostrerà.
 */
export const miaLezioneSchema = z.object({
    id: z.uuid(),
    courtId: z.uuid(),
    courtName: z.string(),
    date: z.string(),
    time: z.string(),
    durationMinutes: z.number().int(),
    /** `lezione`, `corso` — o qualunque altra cosa a cui la segreteria l'abbia messo. */
    type: z.string(),
    title: z.string().nullable(),
    status: z.string(),
    /**
     * 🕰️⭐⭐ **È GIÀ FINITA? LO DICE IL SERVER, non l'orologio di chi guarda.**
     *
     * ⚖️ La schermata di una lezione passata è **un'altra schermata**: si corregge
     * chi c'era, e il resto no (⇒ `calendario/regole.ts`, *«il passato che non si
     * tocca è QUANDO e DOVE si è giocato, non chi c'era»*). Per disegnarla serve
     * sapere se è finita, e il browser non è la fonte giusta: un telefono indietro
     * di un'ora mostrerebbe i bottoni dello spostamento su una lezione che il
     * server rifiuta di spostare — cioè un gesto che si può fare e non si può
     * ottenere.
     * ⛔ E non si ricava da `date` e `time` da questa parte: sarebbe un secondo
     *    posto in cui si decide cos'è passato, e il primo è `controllaPassato`.
     */
    passata: z.boolean(),
    /**
     * 🎓⭐ **A quale corso appartiene questa volta**, o `null` se non è di un corso.
     *
     * ⚖️ Serve per una riga sola — «Fa parte di» — e vale il campo: senza,
     * annullando una volta e chiedendo di recuperarla, il maestro non saprebbe
     * **quale** corso sta allungando. E la serie viaggia già dentro la
     * prenotazione (⇒ `SERIE_JOIN`): non è una lettura in più, è una riga che
     * smette di essere buttata via.
     */
    serieId: z.uuid().nullable(),
    /**
     * 📝⭐⭐ **L'APPUNTO DEL MAESTRO SU QUESTA VOLTA — tappa ⑦a, 20/09/2026.**
     *
     * ⚖️ **Non è `notes`**, che è della segreteria e resta fuori da questo schema
     * per la ragione scritta qui sopra. Sono due note di **due persone diverse
     * sullo stesso fatto**: su un campo solo, chi scrive per secondo cancella
     * l'altro (⇒ `migrations/1758380000000_la_mia_nota.cjs`).
     *
     * 🔇 **È muta, e lo schermo lo dice invece di lasciarlo sperare**: fra i sette
     * gesti che avvisano non ce n'è uno per «ha scritto un appunto», e non se ne
     * inventa un ottavo per una riga che nessun socio deve leggere.
     */
    nota: z.string().nullable(),
    /**
     * 🔄⭐⭐ **QUESTA LEZIONE NON È PIÙ TUA: LA TIENE LUI — tappa ⑦c, 20/09/2026.**
     *
     * 📏 Il difetto che questo campo esiste per togliere: l'agenda è `where
     * instructor_id = $1`, quindi il giorno in cui la segreteria dà la lezione a
     * un altro quella riga **sparisce e basta** — e il maestro se ne accorge
     * andando al campo. ⇒ La riga resta, e **dice chi la tiene**: «sparita» e
     * «data a Giorgio» sono due notizie diverse, e solo la seconda evita un
     * viaggio.
     *
     * ⛔ **Nullo vuol dire «è tua»**, e non «non lo so»: le lezioni che arrivano
     *    all'area maestro sono le sue, più quelle che **erano** sue e non sono
     *    ancora finite (⇒ `maestro/deposito.ts`). Quando non è nullo, su questa
     *    scheda **non c'è nessun gesto**: le scritture passano da un'altra
     *    lettura, che di lezioni non sue non ne trova (⇒ `miaPrenotazione`).
     */
    passataA: z.string().nullable(),
    /**
     * 🔄 **E il verso opposto: te l'hanno passata.**
     *
     * ⚖️ Serve per la stessa ragione, letta al contrario: senza, in agenda
     * comparirebbe una lezione che il maestro **giurerebbe di non aver mai
     * prenotato** — e la prima cosa che farebbe è telefonare in segreteria per
     * chiedere di chi è.
     * ⛔ È nullo anche quando `passataA` non lo è: una lezione che non è più tua
     *    non è una lezione che ti è arrivata.
     */
    arrivataDa: z.string().nullable(),
    players: z.array(allievoInCampoSchema),
    /** 💶 La somma degli importi a carico. Non è un incasso, ed è scritto due volte apposta. */
    totalDueCents: z.number().int(),
});
export const miaGiornataSchema = z.object({
    date: z.string(),
    items: z.array(miaLezioneSchema),
});
export const domandaGiornataSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Serve un giorno come 2026-09-18'),
});
// ── L'agenda: quattordici giorni in una volta ────────────────────────────────
/**
 * ⭐⭐ **UNA CHIAMATA PER QUATTORDICI GIORNI, e non è un'ottimizzazione: è la
 *    schermata che cambia.**
 *
 * ⚖️ `GET /giornata` risponde a *«cosa ho oggi»*, ed è giusta finché l'agenda è
 * un giorno per volta con le frecce. L'agenda si **sfoglia**: sette giorni
 * indietro e sei avanti, di seguito, col datario che dice quanto è pieno
 * ciascuno. Chiederli uno per uno vorrebbe dire **quattordici** richieste per
 * disegnare una schermata — e quattordici attese diverse, cioè una pagina che
 * si compone a pezzi sotto le dita mentre si scorre.
 * ⛔ **Non sostituisce `GET /giornata`**: quella resta la risposta a un'altra
 *    domanda, e la usano le schermate che parlano di **un** giorno.
 */
export const giornoDellAgendaSchema = z.object({
    date: z.string(),
    items: z.array(miaLezioneSchema),
    /**
     * Quanti minuti di quel giorno sono già suoi — la riga sotto la tacca del
     * datario. Si conta **qui** e non nel browser: una somma fatta a schermo è il
     * modo di ritrovarsi due numeri diversi per lo stesso giorno, e quello sotto
     * la tacca è il primo che si guarda.
     */
    minutiOccupati: z.number().int(),
});
/**
 * 🕰️⭐⭐ **`adesso` VIENE DAL SERVER, ed è la riga che conta di questo schema.**
 *
 * ⚖️ Un'agenda che si apre su «oggi» deve sapere **qual è oggi per il circolo**.
 * Il browser sa qual è il suo: un telefono con l'ora storta, o un maestro che
 * scrive da un altro fuso, marcherebbero «Oggi» un giorno che non lo è — e la
 * schermata si aprirebbe sul posto sbagliato **senza nessun errore**. ⇒ Il
 * giorno e il minuto li dice **Postgres**, che è già la fonte con cui
 * quest'area evita di proporre ore passate (⇒ `maestro/deposito.ilSuoGiorno`).
 * 📌 *Due orologi danno due verità: quella che conta è quella del circolo,
 *    perché è lì che si va a giocare.*
 */
export const miaAgendaSchema = z.object({
    dal: z.string(),
    giorni: z.array(giornoDellAgendaSchema),
    adesso: z.object({ date: z.string(), minuti: z.number().int() }),
});
export const domandaAgendaSchema = z.object({
    dal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Serve un giorno come 2026-09-18'),
    /**
     * ⚠️ Il tetto non è tirchieria: senza, una richiesta da mille giorni farebbe
     * leggere al database mille giorni di prenotazioni per disegnare una striscia
     * lunga quanto uno schermo.
     */
    giorni: z.coerce.number().int().min(1).max(31).default(14),
});
// ── Cercare un allievo ───────────────────────────────────────────────────────
/**
 * 🔒⭐⭐ **DUE CHIAVI, E BASTA.** È lo schema che rende vera la decisione sua —
 * 🗣️ *«cerca i soci per nome, e vede solo il nome»*.
 *
 * ⚖️ **Perché non si riusa `personaTrovataSchema` del calendario**, che oggi ha
 * esattamente questi due campi: oggi. Il giorno in cui qualcuno gli aggiunge il
 * telefono — che è la cosa più naturale del mondo su una schermata della
 * segreteria — quel campo comincerebbe a uscire **anche di qui**, senza che
 * nessuno abbia aperto questo file. 📌 *Uno schema riusato è un canale riusato.*
 */
export const socioPerMaestroSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
});
export const elencoSociPerMaestroSchema = z.object({ items: z.array(socioPerMaestroSchema) });
export const domandaSociPerMaestroSchema = z.object({
    q: z.string().trim().min(2, 'Servono almeno due lettere').max(80),
    limit: z.coerce.number().int().min(1).max(25).default(10),
});
/**
 * Chi mettere in campo: **un socio, oppure il nome di un ospite**.
 *
 * 🔄🚨⭐⭐ **L'OSPITE È ENTRATO IL 20/09/2026, E QUESTA RIGA DICEVA IL
 *    CONTRARIO.** Qui c'era scritto *«un socio, e solo un socio»*, e il perché
 *    era: *«l'ospite non è stato deciso da nessuno… quando servirà, sarà una
 *    decisione, non un campo in più»*. ⇒ La decisione è arrivata:
 *    🗣️ **«sì, solo sulle lezioni singole»** (19/09).
 *    ⚖️ La riga vecchia non era sbagliata quando fu scritta: descriveva
 *    un'assenza di decisione, non un divieto — e si **corregge**, non si
 *    affianca.
 *
 * ⛔ **Cosa NON è entrato con lui, ed è rimasto fuori per la stessa ragione di
 *    prima**: l'**importo**. Lo propone il listino, come per la segreteria. Un
 *    maestro che scrive quanto deve un allievo deciderebbe un prezzo, e il
 *    prezzo non è suo (🗣️ *«vede gli importi, non incassa»*).
 *    ⇒ Per questo **non si riusa `postoInCampoSchema`** del calendario, che ha
 *    esattamente questi due campi **più `amountDueCents`**: sarebbe un canale
 *    riusato, e il campo in più arriverebbe di qui senza che nessuno apra
 *    questo file. 📌 *Uno schema riusato è un canale riusato.*
 *
 * ⚠️ **O l'uno o l'altro, mai tutti e due**: un ospite che porta anche un
 *    `memberId` sarebbe una persona con due identità nella stessa ora, e il
 *    giorno dell'incasso non si saprebbe a chi addebitare. È la stessa `refine`
 *    del calendario, e lo è di proposito — la regola è una, e le due porte la
 *    dicono con le stesse parole.
 *
 * 📏 **I limiti del nome sono quelli della segreteria** (1–120 caratteri, e
 *    tagliato ai bordi) e non due nuovi: la colonna è la stessa
 *    (`booking_players.guest_name`) e l'elenco che li mostra pure ⇒ un limite
 *    più stretto di qua vorrebbe dire un nome che la segreteria accetta e il
 *    maestro non può scrivere, senza che nessuna regola lo chieda.
 *
 * 🚨 **DOVE un ospite può stare non lo dice questo schema**, e non è una
 *    dimenticanza: *«solo sulle lezioni singole»* è una regola sul **tipo della
 *    prenotazione**, che un corpo di richiesta non conosce
 *    (⇒ `controllaDoveVaUnOspite`, `modules/maestro/ospiti.ts`).
 */
export const metteInCampoSchema = z
    .object({
    memberId: z.uuid().nullable().default(null),
    guestName: z.string().trim().min(1).max(120).nullable().default(null),
})
    .refine((v) => (v.memberId === null) !== (v.guestName === null), {
    message: 'Serve un socio oppure il nome di un ospite, non tutti e due',
    path: ['guestName'],
});
/**
 * 🚨⭐⭐ **«NON C'ERA» — e paga lo stesso.**
 *
 * ⚖️ **Un corpo con un booleano, e non due rotte** (`POST …/assenza` +
 * `DELETE …/assenza`): «assente» e «presente» sono lo **stesso fatto** letto
 * nei due versi, e l'elenco congelato delle rotte dell'area cresce di uno
 * invece che di due. 📌 *Due rotte per un interruttore sono due posti in cui
 * la stessa regola può divergere.*
 *
 * ⛔ **Niente `motivo`**: perché qualcuno non è venuto non è un dato del
 * gestionale — è una cosa che si dicono a bordo campo. Un campo di testo
 * libero accanto al nome di un socio è un posto in cui, prima o poi, finisce
 * scritto qualcosa che nessuno voleva conservare.
 */
export const segnoLAssenzaSchema = z.object({ assente: z.boolean() });
// ── Prenotare ────────────────────────────────────────────────────────────────
/**
 * Uno spazio dove il maestro può mettersi: **un campo, un'ora, una durata**.
 *
 * 🚨⭐⭐ **UN ELENCO, NON UNA GRIGLIA, ed è una misura e non un gusto.** La
 * griglia della segreteria è larga `4.5rem + N×min 7rem` (⇒ `Griglia.tsx`),
 * cioè **520 px minimi** con quattro campi, contro i **390** di un telefono:
 * si scorrerebbe in orizzontale per leggere un'ora.
 * ⚖️ E non è solo larghezza: una griglia serve a **confrontare** i campi fra
 * loro, che è il mestiere di chi incastra la giornata del circolo. Al maestro
 * non serve confrontare — gli serve **una** ora libera dove sta lui.
 */
export const spazioDelMaestroSchema = z.object({
    courtId: z.uuid(),
    courtName: z.string(),
    time: z.string(),
    durationMinutes: z.number().int(),
});
/**
 * ⭐ `perimetroAperto` esiste per **distinguere due vuoti**, che a schermo
 * sarebbero lo stesso e chiedono due cose opposte:
 *   · *il circolo non ti ha aperto niente questo giorno* ⇒ si telefona alla
 *     segreteria, ed è l'unica che può cambiarlo;
 *   · *le tue ore ci sono, ma sono tutte occupate* ⇒ si guarda un altro giorno.
 * 📌 *Un elenco vuoto senza il suo perché è una schermata che sembra rotta.*
 */
/**
 * ⭐⭐ **UNA FASCIA DELLA SUA GIORNATA: un pezzo di tempo, libero o no.**
 *
 * 🗣️ 19/09/2026: *«potrebbe essere fatto a scorrimento verticale facendomi
 * vedere sia quelli liberi che quelli occupati con il riferimento agli slot»*.
 *
 * ⚖️ **Perché viaggia INSIEME a `items` e non al posto suo.** Sono due domande
 * diverse: `items` risponde a *«quali inizi posso prendere»* — ed è quello che
 * serve a chi cerca **un campo a un'ora data** (⇒ la riga «Dove» della scheda);
 * le fasce rispondono a *«com'è fatta la mia giornata»*, che è la domanda con
 * cui si apre la schermata. ⛔ E non si contraddicono: gli `inizi` di una fascia
 * sono **gli stessi** `items`, ritagliati (⇒ `fasceDelMaestro`).
 *
 * 🔒🔒 **`etichetta` È NULLA SU TUTTO CIÒ CHE NON È SUO, ed è la riga di
 *    sicurezza di questo schema.** Il maestro deve sapere che un'ora è presa —
 *    se no non capisce perché non gliela si offre — ma **da chi** lo riguarda
 *    solo quando è lui stesso. ⇒ Sulla prenotazione di un altro maestro, o di
 *    una partita della segreteria, qui non esce niente: nessun nome, nessun
 *    titolo, e nemmeno l'identificativo con cui chiederli.
 * 📌 *«Occupato» è un fatto del campo; «occupato da Rossi» è un fatto di Rossi,
 *    e non sta dietro questa porta.*
 */
export const fasciaDelMioGiornoSchema = z.object({
    courtId: z.uuid(),
    courtName: z.string(),
    startsAtTime: z.string(),
    endsAtTime: z.string(),
    stato: z.enum(['libera', 'corta', 'occupata', 'chiusa']),
    /** Gli inizi prendibili dentro una fascia libera. Vuoto su tutte le altre. */
    inizi: z.array(z.string()),
    /** Cos'è, **solo se è sua**: «Lezione · Paolo F.», il nome del corso. */
    etichetta: z.string().nullable(),
    /** La sua lezione, per andarci sopra dall'elenco. Nullo su quella di un altro. */
    lezioneId: z.uuid().nullable(),
});
export const mieiSpaziSchema = z.object({
    date: z.string(),
    durationMinutes: z.number().int(),
    perimetroAperto: z.boolean(),
    /**
     * 🏖️🆕⭐⭐ **IL TERZO VUOTO, e senza di lui gli altri due dicono una bugia**
     * (tappa ⑥). Un giorno in cui il maestro ha scritto che non c'è arriva qui
     * con `items` vuoto **e** `perimetroAperto: false` — cioè identico al giorno
     * in cui il circolo non gli ha aperto niente, che manda a telefonare in
     * segreteria per una cosa che ha deciso lui e che disfa lui.
     * 📌 *Due stati indistinguibili a schermo sono uno stato solo, e il
     *    messaggio che li accompagna è giusto al massimo per metà delle volte.*
     * ⚖️ Si legge **per primo**: quando è vero, cosa dicano gli altri due non
     *    importa più.
     */
    via: z.boolean(),
    items: z.array(spazioDelMaestroSchema),
    fasce: z.array(fasciaDelMioGiornoSchema),
});
export const domandaSpaziSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Serve un giorno come 2026-09-18'),
    durationMinutes: z.coerce.number().int().min(5).max(600).default(60),
    /**
     * 🔁 La prenotazione da **non** contare come occupata: serve quando si sposta
     * una lezione, che altrimenti si vedrebbe occupare l'ora in cui già sta — e
     * l'unico spazio che non comparirebbe sarebbe il suo.
     * ⛔ Dev'essere una **sua** lezione: l'id passa per la stessa `where` di tutte
     *    le altre, quindi quello di un altro maestro non esclude niente.
     */
    escludi: z.uuid().nullable().default(null),
});
/** Dove e quando: le quattro coordinate che prenotare e spostare hanno in comune. */
const spazioSchema = z.object({
    courtId: z.uuid(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    durationMinutes: z.number().int().min(5).max(600),
});
/** 🏋️ Cosa può prenotare un maestro da sé (voce 21): il corso ha la sua porta. */
export const TIPI_DEL_MAESTRO = ['lezione', 'allenamento'];
/**
 * Prenotare una lezione, o un allenamento.
 *
 * ⛔ **Niente `instructorId`, e non è una semplificazione**: lo mette il server
 * — **lui**. Se arrivasse nel corpo, esisterebbe una richiesta che chiede di
 * prenotare per un altro maestro, e l'unica cosa che la fermerebbe sarebbe un
 * controllo da ricordarsi di scrivere.
 * 📌 *Ciò che non si può chiedere non si può nemmeno sbagliare a controllare.*
 *
 * 🔄 **Il tipo invece ora si chiede, ma fra DUE soli** — voce 21, 24/09/2026:
 * 🗣️ *«la app del maestro non posso scegliere allenamento, ma c'è solo lezione
 * e corso»*. Qui c'era scritto «niente `type`»: la stessa ragione regge, perché
 * l'elenco chiuso è la difesa — un `corso` o una `partita` non passano lo
 * schema. Assente vuol dire `lezione`, com'era prima.
 *
 * ⏳ E niente allievi: si mettono dopo, dalla scheda della lezione, che è una
 * schermata che esiste già (tappa ③). Un passo per schermata.
 */
export const prenotoIoSchema = spazioSchema.extend({
    tipo: z.enum(TIPI_DEL_MAESTRO).optional(),
});
/**
 * Spostare una sua lezione: **uno spazio intero**, non un campo per volta.
 *
 * ⚖️ La segreteria modifica una prenotazione pezzo per pezzo (trascinandola
 * cambia campo e ora, dalla scheda cambia la durata). Il maestro invece sceglie
 * **uno spazio dall'elenco**, che porta con sé campo, giorno, ora e durata già
 * insieme — quindi qui arrivano tutti e quattro, e non c'è nessuno stato
 * intermedio in cui metà della collocazione è quella nuova e metà la vecchia.
 */
export const spostoIoSchema = spazioSchema;
/**
 * ⏱️ **Cambiare quanto dura, e NIENTE ALTRO** — 18/09/2026, 🗣️ *«mi devi far
 * modificare la durata di una lezione o di un corso»*.
 *
 * ⚖️ **Perché non si riusa `spostoIoSchema`**, che porta già la durata insieme
 * alle altre tre coordinate: spostare vuol dire *scegliere uno spazio libero* —
 * la schermata mostra l'elenco delle ore, e chi sposta sta guardando **dove**.
 * Allungare invece è una cosa che si fa **restando dov'è**, e chiedere anche
 * campo, giorno e ora vorrebbe dire che il telefono li rimanda tali e quali:
 * quattro valori spediti per cambiarne uno, e tre occasioni di rimandare indietro
 * quello che intanto ha cambiato qualcun altro.
 * 📌 *Un gesto che tocca una cosa sola manda una cosa sola: quello che non si
 *    manda non si può sovrascrivere per sbaglio.*
 */
export const cambioLaDurataSchema = z.object({ durationMinutes: z.number().int().min(5).max(600) });
// ── I corsi, con gli iscritti fissi ──────────────────────────────────────────
/**
 * Una persona iscritta a un corso, **come la vede il maestro**.
 *
 * ⚖️ **Non è `allievoInCampoSchema`**, e le due si somigliano abbastanza da
 * confondersi: quella è una persona **in campo un giorno** (l'`id` è la riga in
 * campo, e togliendola si toglie da quel giorno soltanto); questa è una persona
 * **iscritta al corso** — l'`id` è l'iscrizione, e togliendola si esce da tutte
 * le volte che restano. ⇒ Due identificativi diversi per due gesti diversi:
 * scambiarli vorrebbe dire togliere un allievo da sei lunedì credendo di
 * segnarlo assente a uno.
 * 📌 *Due schemi che portano gli stessi campi non sono lo stesso schema se i
 *    loro `id` aprono due porte diverse.*
 */
export const iscrittoAlCorsoSchema = z.object({
    /** L'ISCRIZIONE, non la riga in campo (⇒ il commento qui sopra). */
    id: z.uuid(),
    displayName: z.string(),
    /** Da quale giorno è iscritto: chi entra a metà corso non ha le volte di prima. */
    dal: z.string(),
    /**
     * 💶⭐⭐ **IL TOTALE DEL CORSO, NON LA QUOTA DI UNA VOLTA — e non è una scelta
     * di presentazione.**
     *
     * 📏 `proponiImporto` divide il prezzo del campo per **quattro fisse**
     * (`GIOCATORI_PER_QUOTA`, e il perché sta lì: una richiesta sulla capienza non
     * è una richiesta sul listino). ⇒ In un corso da sei, la quota di una volta è
     * quella di una lezione da quattro, e mostrarla da sola direbbe a un maestro
     * un numero che non è quello che quella persona pagherà.
     * ⚖️ La **somma** invece è un fatto che non dipende dal divisore: è quanto
     * risulta a carico di quella persona su questo corso, contando le volte che
     * ci sono davvero.
     */
    totalDueCents: z.number().int(),
});
/**
 * Una volta del corso: una data, e chi c'era in campo **quel giorno**.
 *
 * 🔄🚨⭐⭐ **QUESTA RIGA DICEVA IL CONTRARIO, ed è la tappa ④a a smentirla.**
 * Qui stava scritto: *«`inCampo` è ciò che rende visibile l'assenza senza
 * inventarle una tabella: la schermata scrive “iscritti 6 · questa volta 5” e
 * il maestro capisce da sé che qualcuno è stato tolto»*. ⇒ **Non regge più**,
 * e per una ragione che non è di presentazione: quella differenza si vedeva
 * perché togliere una persona le **cancellava la riga** — e con la riga se ne
 * andava l'importo, cioè il gestionale faceva l'opposto di *«il corso si paga
 * al mese»*.
 * ⇒ Adesso l'assente **resta fra i giocatori**, con il suo conto, e i numeri
 * sono **due**: `inCampo` (chi c'era davvero) e `assenti`. La riga vecchia si
 * corregge, non si affianca.
 * 📌 *Un'assenza dedotta da una differenza si vede solo finché l'assente
 *    sparisce — cioè solo finché il sistema sta sbagliando la cosa accanto.*
 */
export const voltaDelCorsoSchema = z.object({
    /** La prenotazione: è da lì che si apre la scheda di quel giorno. */
    id: z.uuid(),
    date: z.string(),
    time: z.string(),
    durationMinutes: z.number().int(),
    courtName: z.string(),
    status: z.string(),
    /** Quanti c'erano davvero: i giocatori di quel giorno, **meno** gli assenti. */
    inCampo: z.number().int(),
    /** Quanti erano segnati assenti. Pagano lo stesso: il posto era loro. */
    assenti: z.number().int(),
    /** Già finita: non si tocca più, e la schermata non deve far credere il contrario. */
    passata: z.boolean(),
});
/**
 * ⏳⭐⭐ **CHI ASPETTA UN POSTO — tappa ⑦b, 20/09/2026.**
 *
 * 🗣️ Sua decisione del 19/09: **«in attesa, e lo fai entrare tu»**.
 *
 * ⚖️ **Ci sono DUE id, e non è una svista**: `id` è la riga dell'attesa — serve
 * a **toglierla** — e `memberId` è la persona, che serve a **farla entrare**,
 * perché iscrivere vuole un socio. ⇒ Sono i due gesti che questa riga porta, e
 * ciascuno ha il suo.
 * 📌 *Un identificativo solo su una riga che apre due porte è la svista che un
 *    giorno toglie qualcuno credendo di farlo entrare.*
 *
 * ⛔ **Niente `guestName`**: chi esce da qui entra nel corso, e in un corso ci
 *    vanno gli iscritti (⇒ `maestro/ospiti.ts`). La colonna non esiste proprio.
 */
export const inAttesaSchema = z.object({
    /** La riga dell'attesa: è questa che si toglie. */
    id: z.uuid(),
    /** La persona: è questa che si iscrive. */
    memberId: z.uuid(),
    displayName: z.string(),
    /** Da quando aspetta. Serve a una riga sola, ed è quella che dà l'ordine. */
    dal: z.string(),
});
/**
 * Mettere qualcuno in lista d'attesa.
 *
 * ⚖️ **Non si riusa `iscrivoAlCorsoSchema`**, che porta gli stessi identici
 * campi: due gesti diversi hanno due schemi diversi anche quando si somigliano
 * — è la stessa riga di `iscrittoAlCorsoSchema` contro `allievoInCampoSchema`.
 * Il giorno in cui uno dei due prende un campo in più, l'altro non se lo trova
 * addosso.
 */
export const mettoInAttesaSchema = z.object({ memberId: z.uuid() });
/**
 * Un corso per esteso: le sue volte e i suoi iscritti.
 *
 * ⚠️ **L'identificativo è quello della SERIE, non di una prenotazione**, ed è la
 * differenza fra un corso e una lezione: una lezione è una riga, un corso è il
 * filo che tiene insieme molte righe. Iscriversi a una di quelle righe sarebbe
 * esserci un giorno solo.
 */
export const mioCorsoSchema = z.object({
    serieId: z.uuid(),
    title: z.string().nullable(),
    cadenza: z.string(),
    /** Il primo e l'ultimo giorno che il corso copre davvero: contati, non chiesti. */
    dal: z.string(),
    al: z.string(),
    iscritti: z.array(iscrittoAlCorsoSchema),
    /**
     * ⏳🆕 **Chi aspetta un posto — tappa ⑦b, 20/09/2026.**
     *
     * ⭐ In **ordine di arrivo**, che è la sola regola che una coda può avere
     * senza che qualcuno debba deciderla. Vuoto quasi sempre, e allora la
     * schermata non mostra niente: una sezione «In attesa: nessuno» sarebbe una
     * riga che occupa spazio per dire che non c'è niente da fare.
     */
    attesa: z.array(inAttesaSchema),
    /**
     * 🆕 **Quanti posti restano**, contato dal server e non dalla schermata.
     *
     * ⚖️ La capienza è una regola del gestionale (`CAPIENZA.corso`), non un numero
     * che ogni schermata si ricalcola: due posti che contano i posti liberi sono
     * due posti che un giorno dicono numeri diversi — e quello che sbaglia è
     * quello che decide se offrire «fai entrare».
     * ⚠️ È contato sugli **iscritti**, cioè sul corso: una singola volta può
     *    essere più piena (qualcuno aggiunto a mano quel giorno). A dirlo per
     *    davvero è il server quando si iscrive, con le volte `saltate`.
     */
    postiLiberi: z.number().int(),
    volte: z.array(voltaDelCorsoSchema),
});
/**
 * Un corso nell'elenco: quel tanto che serve a riconoscerlo da un telefono.
 *
 * ⛔ **Senza gli iscritti e senza le volte**: un maestro con dieci corsi da sei
 * mesi si porterebbe dietro centinaia di righe per leggere dieci titoli. Chi
 * apre il corso le chiede allora.
 */
export const corsoInElencoSchema = z.object({
    serieId: z.uuid(),
    title: z.string().nullable(),
    cadenza: z.string(),
    dal: z.string(),
    al: z.string(),
    courtName: z.string(),
    time: z.string(),
    durationMinutes: z.number().int(),
    quantiIscritti: z.number().int(),
    /** Quante volte restano da fare, oggi compreso. Zero = il corso è finito. */
    volteRimaste: z.number().int(),
});
export const mieiCorsiSchema = z.object({ items: z.array(corsoInElencoSchema) });
/**
 * Aprire un corso.
 *
 * ⛔ **Niente allievi qui dentro**, ed è la stessa forma della lezione (⇒
 * `prenotoIoSchema`): si apre il corso, e gli iscritti si mettono dopo, dalla
 * sua scheda. Un passo per schermata, e soprattutto: iscrivere è un gesto che
 * **avvisa delle persone**, e non si mescola con uno che ne crea venti righe.
 *
 * ⛔ E niente `type` e niente `instructorId`: li mette il server — `corso`, e
 * **lui**. 📌 *Ciò che non si può chiedere non si può nemmeno sbagliare a
 * controllare.*
 */
export const aproUnCorsoSchema = z.object({
    courtId: z.uuid(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    durationMinutes: z.number().int().min(5).max(600),
    /**
     * 🏷️ **Il nome è obbligatorio, e per una lezione non lo è.** Un corso vive
     * mesi e si sceglie da un elenco: «Corso» e «Corso» e «Corso» sono tre righe
     * che nessuno distingue. Una lezione invece si riconosce dall'ora, perché la
     * si guarda dentro la giornata in cui sta.
     */
    title: z.string().trim().min(2, 'Dai un nome al corso').max(120),
    /**
     * ⚖️ **DUE cadenze e non le otto del calendario**, ed è una scelta dichiarata,
     * non una dimenticanza: le altre sei (ogni giorno, ogni due giorni, ogni tre
     * settimane, mensile…) restano alla segreteria, che le ha e le sa usare. Un
     * corso si fa a settimane, e un elenco di otto voci su un telefono è un elenco
     * in cui si tocca quella sbagliata.
     * 🔓 Aggiungerne una qui non rompe niente: si srotolano tutte con la stessa
     *    funzione (`giorniDellaSerie`). È una scelta di **schermata**, e si cambia
     *    il giorno in cui lui la chiede.
     */
    cadenza: z.enum(['settimanale', 'quindicinale']),
    fino: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/**
 * Iscrivere qualcuno: **un socio, e basta**.
 *
 * ✅⭐⭐ **IL GIORNO È ARRIVATO, ed è il 20/09/2026.** Qui c'era scritto al
 *    futuro: *«il giorno in cui `metteInCampoSchema` ammettesse un ospite — un
 *    caso che qualcuno prima o poi chiederà per una lezione — l'ospite
 *    comincerebbe ad arrivare anche qui»*. ⇒ Quel giorno è **oggi**
 *    (🗣️ *«sì, solo sulle lezioni singole»*), e siccome i due schemi sono
 *    rimasti **due**, non è arrivato niente: questa riga è ancora
 *    `z.object({ memberId })` senza che nessuno l'abbia difesa.
 *    ⚖️ La frase si corregge dal futuro al **passato** perché ha smesso di
 *    essere una previsione: è un fatto misurato.
 *    📌 *Uno schema riusato è un canale riusato — e il giorno in cui si scopre
 *       non è il giorno in cui si separano: è quello in cui uno dei due cambia.*
 *
 * ⛔ **E il perché un iscritto non può essere un ospite regge intero**: un
 *    iscritto senza id non riceverebbe mai un avviso, e il suo posto — che si
 *    paga al mese, volta dopo volta — resterebbe a carico di un nome
 *    (⇒ `modules/maestro/ospiti.ts`, dov'è scritto per esteso).
 */
export const iscrivoAlCorsoSchema = z.object({ memberId: z.uuid() });
/**
 * Una volta che non si è potuta fare, e **il perché**.
 *
 * ⚠️ Il motivo non è un ornamento: una scrittura parziale si accetta solo se chi
 * l'ha chiesta sa **quale metà** è mancata (⇒ `serieCreataSchema`, dove la stessa
 * regola è scritta per la creazione di una serie).
 */
export const voltaSaltataSchema = z.object({ date: z.string(), perche: z.string() });
/** Com'è andata ad aprire un corso. */
export const corsoApertoSchema = z.object({
    serieId: z.uuid(),
    /** Quante volte sono nate davvero, la prima compresa. */
    quante: z.number().int(),
    saltate: z.array(voltaSaltataSchema),
    corso: mioCorsoSchema,
});
/**
 * ⏱️ Com'è andata a cambiare la durata di un corso.
 *
 * ⚖️ Porta gli stessi tre campi di `esitoIscrizioneSchema` e **non è quello**:
 * là `volte` conta le date su cui una persona è **entrata**, qui le date che si
 * sono **allungate**. Due numeri che si chiamano uguale e contano due cose
 * diverse stanno meglio in due schemi, o il giorno in cui uno dei due cambia
 * forma se lo porta dietro l'altro (⇒ *«uno schema riusato è un canale
 * riusato»*).
 */
export const durataDelCorsoSchema = z.object({
    /** Quante volte si sono allungate davvero. */
    volte: z.number().int(),
    saltate: z.array(voltaSaltataSchema),
    corso: mioCorsoSchema,
});
/**
 * Com'è andata a iscrivere o a disiscrivere qualcuno.
 *
 * ⭐ `volte` è la cosa che il maestro deve vedere: *«iscritto, e messo in campo
 * su 6 volte»* è un'informazione; *«fatto»* non lo è — e la differenza fra 6 e 4
 * è esattamente quello che questa rotta può aver saltato.
 */
export const esitoIscrizioneSchema = z.object({
    volte: z.number().int(),
    saltate: z.array(voltaSaltataSchema),
    corso: mioCorsoSchema,
});
// ── Governare un corso: il nome, quante volte, e la fine ─────────────────────
//
// 🎓⭐⭐ **PERCHÉ QUESTI TRE GESTI STANNO INSIEME, e non con lo spostamento.**
//    Nome, numero di volte e «esiste ancora» sono ciò che un corso **è**: sono
//    le cose con cui lo si vende («il corso da otto del lunedì»). Giorno, ora e
//    campo sono invece dove il circolo lo mette, e restano della segreteria.
// 📌 *Governare non è spostare: la prima domanda è «cos'è questo corso», la
//    seconda «dove lo metto» — e le rispondono due persone diverse.*
/**
 * 🏷️ Come si chiama un corso.
 *
 * ⚖️ Stesso vincolo del titolo alla nascita (⇒ `aproUnCorsoSchema`): due lettere
 * almeno. Un corso senza nome sull'elenco diventa «Corso» come tutti gli altri,
 * e un elenco di cose che si chiamano tutte uguale non è un elenco.
 * 🔇 **E non parte nessun avviso**: fra i sette gesti non ce n'è uno per «si
 *    chiama diversamente», e non se ne inventa un ottavo per cambiare una
 *    frase (⇒ `avvisi.ts`). Lo schermo lo dice, invece di lasciarlo credere.
 */
export const rinominoIlCorsoSchema = z.object({
    title: z.string().trim().min(2, 'Ci vogliono almeno due lettere').max(120),
});
/** Com'è andata la rinomina: `volte` è quante portano il nome nuovo. */
export const corsoRinominatoSchema = z.object({
    volte: z.number().int(),
    corso: mioCorsoSchema,
});
/**
 * 🔢⭐⭐ **QUANTE VOLTE IN TUTTO, comprese quelle già fatte** — e non quante ne
 * restano.
 *
 * ⚖️ È il numero con cui il corso si vende: *«un corso da otto»* resta un corso
 * da otto anche alla sesta lezione. Un residuo che cala ogni settimana non è un
 * dato che qualcuno ha scelto — è il calendario che scorre, e metterlo qui
 * vorrebbe dire far scrivere al maestro un numero diverso ogni lunedì per dire
 * sempre la stessa cosa.
 * ⛔ Un numero **non più alto delle volte già fatte** si rifiuta: cancellerebbe
 *    il passato, che è la sola cosa che nessun gesto di questa area può fare.
 */
export const cambioLeVolteSchema = z.object({ volte: z.number().int().min(1).max(120) });
/**
 * Com'è andata ad allungare o ad accorciare.
 *
 * ⚖️ `nate` e `tolte` sono **due numeri e non uno con il segno**: allungare e
 * accorciare non sono lo stesso fatto visto da due parti — uno chiede al circolo
 * dei campi che potrebbe non dare, l'altro toglie delle ore che erano promesse.
 * Un solo numero costringerebbe chi legge a dedurre quale dei due è successo.
 */
export const volteDelCorsoSchema = z.object({
    nate: z.number().int(),
    tolte: z.number().int(),
    saltate: z.array(voltaSaltataSchema),
    corso: mioCorsoSchema,
});
/**
 * Com'è andata ad annullare un corso intero.
 *
 * ⛔ **Non porta `corso`, e non è una dimenticanza**: annullate le volte che
 * restavano, `mioCorso` rende `null` — il corso non c'è più da mostrare. Farne
 * tornare una fotografia vuota sarebbe far credere che ci sia ancora qualcosa
 * da aprire.
 * ⭐ `avvisati` è il numero di **persone**, non di messaggi: è il modo di sapere
 *    che è partito un avviso a testa e non uno per data.
 */
export const corsoAnnullatoSchema = z.object({
    tolte: z.number().int(),
    avvisati: z.number().int(),
});
/**
 * 🔁⭐⭐ **IL RECUPERO SI DECIDE NELL'ATTIMO IN CUI SI ANNULLA, non dopo.**
 *
 * 🗣️ Sua scelta del 19/09/2026: *«il corso finisce una settimana dopo»* — niente
 * conto di crediti per persona. ⇒ È lì che il maestro sa se quella volta il
 * circolo la ridarà, e chiederglielo dopo vorrebbe dire tenere il registro di
 * chi deve cosa.
 * 📌 *Una domanda fatta al momento giusto sostituisce un registro.*
 *
 * ⚖️ `false` è il valore predefinito, ed è il verso giusto: chi non dice niente
 * annulla e basta — aggiungere una volta a un corso è un gesto che va chiesto,
 * non subìto.
 */
export const annulloLaVoltaSchema = z.object({
    recupera: z.boolean().default(false),
    /**
     * 🔄 30/09/2026: era il «sì» alla trattenuta di una lezione della sera
     * disdetta a meno di 48 ore, regola andata via con l'acconto. Resta nel
     * contratto perché l'app del maestro di prima lo può ancora mandare.
     */
    accettoLaTrattenuta: z.boolean().default(false),
});
/**
 * Com'è finita: annullata, e — se lo si era chiesto — dove è stata rimessa.
 *
 * ⛔ **`{ok: true}` non bastava**, ed è la ragione per cui `esitoDelMaestroSchema`
 * è sparito il 20/09 senza lasciare niente al suo posto — non lo usava
 * nessun'altra rotta: i due bottoni della conferma
 * (*«e la recupero alla fine»* contro *«una volta in meno»*) si distinguono solo
 * dalla frase che esce dopo. Senza `recuperata`, la schermata direbbe «fatto» in
 * tutt'e due i casi — cioè non direbbe se la promessa è stata mantenuta.
 * ⚠️ E `perche` esiste perché il recupero **può non riuscire**: quel lunedì il
 *    campo può essere preso, o fuori dalle sue ore. L'annullo resta valido lo
 *    stesso, e il motivo si legge invece di doverlo indovinare da un numero che
 *    non torna.
 */
export const voltaAnnullataSchema = z.object({
    ok: z.literal(true),
    recuperata: z.object({ date: z.string(), time: z.string(), courtName: z.string() }).nullable(),
    perche: z.string().nullable(),
});
// ── I soldi del mese ─────────────────────────────────────────────────────────
/**
 * 💶⭐⭐ **QUANTO VALE IL MESE — e la prima cosa da sapere è cosa NON è.**
 *
 * 🗣️ Sua richiesta fra le otto: *«i soldi del mese: già fatte · ancora da fare ·
 * in tutto, per origine»*. ⛔ E si ferma lì: **nessun incasso**, perché per il
 * maestro l'incassato non esiste — `allievoInCampoSchema` è costruito apposta
 * senza `incassatoCents`, e il deposito ricopia campo per campo per non farlo
 * uscire. 🗣️ *«Vede gli importi, non incassa.»*
 * 📌 *Un riepilogo che promette il saldo promette la cassa di un altro.*
 *
 * ⭐⭐ **E POGGIA SULLA TAPPA ④a, che è la ragione per cui veniva prima.** Il
 * dovuto di una volta è `sum(booking_players.amount_due_cents)` — **assenti
 * compresi, perché la riga c'è**. Nessun ramo «se assente allora…»: da quando
 * l'assenza è una spunta invece di una riga tolta, *«mancare non sconta»* è
 * vero **per costruzione**, e questa somma non deve ricordarselo.
 */
export const domandaSoldiSchema = z.object({
    mese: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Serve un mese come 2026-09'),
});
/**
 * Le etichette delle origini che **non** sono un corso.
 *
 * ⚠️ Il mockup butta tutto il non-corso in «Lezioni singole»: là non esistono
 * altri tipi, qui sì — `bookings_tipo_check` ne ammette **cinque** — e chiamare
 * «lezione» una partita sarebbe una parola sbagliata per un numero giusto.
 * ⚖️ Sta qui e non nella schermata perché a riempire `nome` è il **server**: due
 * mappe, una per sponda, sono due mappe che un giorno dicono nomi diversi per
 * la stessa cosa.
 * 🩹 `corso` c'è per il caso in cui `series_id` sia diventato nullo
 * (`bookings.series_id` è `on delete SET NULL`): quelle volte non hanno più un
 * corso a cui appartenere, e restano contate per tipo invece di sparire.
 */
export const ETICHETTA_ORIGINE = {
    lezione: 'Lezioni singole',
    allenamento: 'Allenamenti',
    partita: 'Partite',
    torneo: 'Tornei',
    corso: 'Corsi',
    manutenzione: 'Manutenzioni',
};
/**
 * Una riga del riepilogo: **da dove vengono** quei soldi.
 *
 * ⭐ `chiave` non si mostra: serve a distinguere due corsi che si chiamano
 * uguale, ed è ciò che la schermata usa per elencarli. Il **nome** è quello che
 * si legge, e due nomi uguali sono un fatto del circolo, non un difetto.
 * 🚫 `annullate` si **conta** e vale **zero**: non le deve nessuno, ma una riga
 * che dicesse «4 volte» dove il maestro ne ricorda cinque sembrerebbe sbagliata.
 */
export const rigaDeiSoldiSchema = z.object({
    chiave: z.string(),
    nome: z.string(),
    volte: z.number().int(),
    annullate: z.number().int(),
    cents: z.number().int(),
});
/**
 * Il mese, in tre numeri e un elenco.
 *
 * ⛔ **Niente `inTuttoCents`**: è `fatteCents + daFareCents`, e un terzo numero
 * mandato dal server è un terzo numero che un giorno può non tornare con gli
 * altri due. Lo somma chi lo mostra, dove si vede subito se non torna.
 * ⏳ Il confine fra i due è la **fine** della volta, non l'inizio: una lezione
 * in corso è «ancora da fare» finché non è finita (è la stessa parola di
 * `passata`).
 * ⭐ `primo` e `ultimo` sono i mesi estremi in cui questo maestro ha qualcosa:
 * servono alle due frecce, che si spengono lì. Sono `null` quando non ha mai
 * avuto niente — che è una cosa diversa da «questo mese zero».
 */
/**
 * 💶⭐⭐ **IL SECONDO LIBRO: quello fra il maestro e il circolo (tappa ⑧c).**
 *
 * 🗣️ *«io affitto il campo al maestro […] costo della lezione oppure del corso,
 * costo del campo, la differenza va al maestro»* · *«incasso io dagli allievi,
 * non il maestro»* (20/09/2026). ⇒ Il circolo incassa, **trattiene** l'ora di
 * campo e paga al maestro la differenza: **il maestro non versa niente**.
 *
 * ⛔⭐ **`allieviFatteCents` NON È `fatteCents`, e la differenza è tutto.**
 *    Quello lì sopra è **tutto** quello che hanno a carico gli allievi; questo è
 *    soltanto la parte sulle volte **coperte da una tariffa**. Una volta che sta
 *    prima della prima tariffa non porta né il suo incasso né il suo costo, come
 *    in segreteria: sommandola qui e non là, «Ti restano» crescerebbe di un
 *    incasso di cui nessuno ha contato il campo.
 * 📌 *Due libri che escludono cose diverse sono due libri che non si possono
 *    confrontare — ed è proprio il confronto la ragione per cui esistono.*
 *
 * ⭐ `tariffaUnica` è `null` quando nel mese ne sono valse **due o più**:
 * nominarne una sarebbe vero a metà, e chi controlla moltiplicando le ore non
 * ritroverebbe il totale. È la stessa scelta della schermata di segreteria.
 *
 * ⛔ E la coppia cifra + data è **riscritta qui**, non importata da `conto.ts`:
 * *«uno schema riusato è un canale riusato»* (⇒ l'intestazione di questo file).
 * Fra le due sponde si condivide la **regola SQL** che sceglie la tariffa, non
 * il canale da cui escono i dati.
 */
export const ilCampoDelMeseSchema = z.object({
    minutiFatte: z.number().int(),
    minutiDaFare: z.number().int(),
    campoFatteCents: z.number().int(),
    campoDaFareCents: z.number().int(),
    allieviFatteCents: z.number().int(),
    allieviDaFareCents: z.number().int(),
    scoperte: z.number().int(),
    tariffaUnica: z
        .object({
        centsPerHour: z.number().int(),
        validFrom: dataSchema,
    })
        .nullable(),
});
export const soldiDelMeseSchema = z.object({
    mese: z.string(),
    fatteCents: z.number().int(),
    daFareCents: z.number().int(),
    righe: z.array(rigaDeiSoldiSchema),
    primo: z.string().nullable(),
    ultimo: z.string().nullable(),
    /**
     * ⛔ **`null` vuol dire «il circolo non ha scritto nessuna tariffa»**, ed è
     * una cosa diversa da «il campo costa zero». Con `null` il riquadro non
     * compare affatto: mostrarlo a zero direbbe al maestro che gli spetta
     * **tutto** l'incasso degli allievi — con dei numeri, che è il modo peggiore
     * di sbagliare.
     */
    campo: ilCampoDelMeseSchema.nullable(),
    /**
     * Le trattenute dal compenso già scritte, una riga per volta col perché.
     * 🔄 30/09/2026: con l'acconto è andata la regola che le scriveva.
     */
    trattenute: z.array(trattenutaDelMaestroSchema).default([]),
    /**
     * 🆕 27/09/2026 (voce 18) — 🗣️ *«ci deve essere una differenza tra l'incassato
     * e quello ancora da incassare»*. Sulle volte **confermate** del mese:
     * · `incassatoCents`: la somma nuda degli incassi dei posti in campo, storni
     *   compresi (sono righe negative) e con ogni metodo;
     * · `daIncassareCents`: posto per posto, quanto deve meno quanto ha pagato,
     *   **mai sotto zero** — chi ha pagato di più non copre chi non ha pagato;
     * · `daIncassareFatteCents`: la parte sulle volte già finite, cioè quella da
     *   cercare per prima.
     * ⛔ Il maestro qui LEGGE e basta: incassa solo la Segreteria.
     */
    incassatoCents: z.number().int(),
    daIncassareCents: z.number().int(),
    daIncassareFatteCents: z.number().int(),
});
/**
 * 🆕 27/09/2026 (voce 19d) — le tariffe in vigore, **solo da leggere**.
 * 🗣️ *«l'app Maestri è solamente una visualizzazione di quello che c'è scritto
 * sul gestionale»*. Le caselle non decise non ci sono: la schermata le dice
 * «non ancora decisa», mai 0 €.
 */
export const tariffeDelMaestroSchema = z.object({
    lezioni: z.array(tariffaLezioneSchema),
    /** Il campo al maestro, in fasce, dal listino dei maestri in vigore oggi. `null`: nessuno. */
    campo: campoAParoleSchema.nullable(),
});
// ── 🏖️ Quando non ci sono ────────────────────────────────────────────────────
//
// 🗣️ **Sua decisione del 19/09/2026**, alla domanda su come un maestro possa
// dire che è in ferie o malato: **«sì, si blocca le ore da sé»**.
//
// ⚖️ **È l'unica cosa del PERIMETRO che scrive lui.** Le bande le apre la
// segreteria e lui le subisce (⇒ `mioPerimetroSchema`, di sola lettura); i
// giorni in cui non c'è li sa solo lui, e chiederglieli per telefono per farli
// scrivere a un altro sarebbe un giro che non protegge niente.
// 📌 *Si dà la penna a chi ha il dato, non a chi ha il ruolo.*
/**
 * Un periodo in cui non c'è, **come si mostra**.
 *
 * ⚠️ **`volte` è il numero di lezioni e volte di corso che stanno LÀ DENTRO, ed
 *    è la cosa più importante di questo schema.** Scrivere un'assenza **non
 *    annulla niente** (⇒ `maestro/assenze.ts`): ciò che era già prenotato resta
 *    in agenda, con dentro delle persone che ci contano. ⇒ Se quel numero non
 *    viaggiasse, l'unico modo di accorgersene sarebbe presentarsi — o non
 *    presentarsi.
 * 📌 *Un gesto che non fa una cosa deve dirlo proprio a chi crede di averla
 *    fatta.*
 */
export const assenzaDelMaestroSchema = z.object({
    id: z.uuid(),
    dalGiorno: z.string(),
    alGiorno: z.string(),
    volte: z.number().int(),
});
export const mieAssenzeSchema = z.object({ items: z.array(assenzaDelMaestroSchema) });
/**
 * Dire che in quei giorni non c'è.
 *
 * ⛔ **Niente motivo, e non è una dimenticanza**: ferie o malattia non le legge
 *    nessuno — questa tabella la segreteria non la vede — e un campo che
 *    nessuno legge è un campo che un giorno qualcuno mostrerà a qualcuno.
 * ⛔ **E niente ore**: si è via a giorni interi. Mezza giornata sarebbe una
 *    banda scritta al contrario, e il posto in cui si decidono le ore ce l'ha
 *    già la segreteria.
 */
export const nonCiSonoSchema = z
    .object({
    dalGiorno: dataSchema,
    alGiorno: dataSchema,
})
    .refine((v) => v.alGiorno >= v.dalGiorno, {
    message: 'L’ultimo giorno viene prima del primo',
    path: ['alGiorno'],
})
    /**
     * 🚨⭐ **UN ANNO È IL TETTO, e serve contro il dito che scivola.** Un'assenza
     * non ha nessun modo di scadere da sé: scritta «dal 2026 al 2036» spegne il
     * perimetro per dieci anni, **in silenzio** — nessun errore, nessun rifiuto,
     * solo una schermata che dice sempre «non puoi prenotare» e un maestro che
     * telefona alla segreteria per una cosa che ha scritto lui.
     * ⚖️ Il tetto non è una regola del circolo: è la misura oltre la quale
     *    «assenza» smette di essere la parola giusta. Chi sta via più di un anno
     *    non è assente, è a riposo — e quello lo scrive la segreteria
     *    (⇒ `instructors.active`).
     */
    .refine((v) => (Date.parse(v.alGiorno) - Date.parse(v.dalGiorno)) / 86400000 < 366, {
    message: 'Un’assenza dura al massimo un anno: per una pausa più lunga, lo scrive la segreteria',
    path: ['alGiorno'],
});
// ── 📝 La sua nota ──────────────────────────────────────────────────────────
//
// 🗣️ La prima delle tre cose marcate «nuovo» sul mockup approvato, e la sua
// decisione del 19/09 su tutte e tre: **«tutte e tre, in coda»**.
/**
 * ⭐ **Quanto può essere lunga, e perché un tetto c'è.**
 *
 * ⚖️ Non è una regola sul contenuto: è la misura oltre la quale «appunto»
 * smette di essere la parola giusta. Una riga di scheda che porta un tema di
 * mille caratteri non è più una nota accanto a una lezione — è un documento in
 * un posto che non sa mostrarlo, e a bordo campo non si legge comunque.
 * ⚠️ E senza tetto lo schema accetterebbe qualunque cosa, compresa una pagina
 *    incollata per sbaglio: il rifiuto arriverebbe dal **database** o da
 *    nessuna parte, cioè nel posto in cui nessuno va a cercare una regola.
 */
export const NOTA_MASSIMA = 500;
/**
 * Scrivere (o cancellare) l'appunto su una volta.
 *
 * ⚖️ **Una stringa e non `string | null`**, ed è una scelta: cancellare è
 * mandare il campo **vuoto**, che è ciò che fa da sé chi svuota la casella e
 * preme «Salva». Ammettere anche `null` vorrebbe dire due modi di dire la
 * stessa cosa già **prima** del database — e il vincolo di là ne ammette uno
 * solo. ⇒ A ridurre i due a uno è `puliscoLaNota`, in un posto solo
 * (⇒ `maestro/nota.ts`).
 */
export const laMiaNotaSchema = z.object({
    nota: z.string().max(NOTA_MASSIMA, `Una nota sta in ${NOTA_MASSIMA} caratteri`),
});
//# sourceMappingURL=maestro.js.map